import { Fragment, useMemo, useState } from "react";
import {
  ChevronDownIcon,
  PauseIcon,
  PlayIcon,
  TrashIcon,
} from "@heroicons/react/outline";
import { CheckIcon, XIcon } from "@heroicons/react/solid";
import { Button, Dialog, Menu } from "ui";

import AlertModal from "../AlertModal";
import { axiosApiInstance } from "../../pages/api/axios";
import { parseErrorsToArray } from "../../utils/parseErrors";
import { Row } from "react-table";
import { toast } from "react-hot-toast";

type SubscriptionBulkActionsProps = {
  selectedRows: Row<{ id: string }>[];
  showSuccessMessage: (options: {
    name?: string;
    message?: string;
    action?: string;
  }) => void;
  setBulkActionErrorIds: React.Dispatch<React.SetStateAction<string[]>>;
  refreshTable: () => void;
  bulkActionsPermissions: null | Record<string, boolean>;
};

type OpenModalType = "pause" | "resume" | "cancel" | "delete";

export default function SubscriptionBulkActions({
  selectedRows,
  refreshTable,
  setBulkActionErrorIds,
  showSuccessMessage,
  bulkActionsPermissions,
}: SubscriptionBulkActionsProps) {
  const [openModal, setOpenModal] = useState<OpenModalType | null>(null);

  const selectedIds = useMemo(
    () => selectedRows.map((row) => row.original.id),
    [selectedRows]
  );

  const doBulkAction = async ({
    type,
    selected,
  }: {
    type: string;
    selected: string[];
  }) => {
    setBulkActionErrorIds([]);

    return axiosApiInstance
      .post(
        `${process.env.NEXT_PUBLIC_API_BASE}/d/subscriptions/bulk_action/`,
        {
          bulk_action: type,
          selected: selected,
        }
      )
      .catch((serverError) => {
        const [bulkActionError] = parseErrorsToArray(serverError);
        const error = bulkActionError as unknown as {
          message: string;
          subscriptions: string[];
        };
        // Bulk actions can pe applied to subset of selected ones
        const isActionAppliedForSomeIDs =
          error.subscriptions.length < selectedIds.length;
        toast.error(error.message);
        setBulkActionErrorIds(error.subscriptions || []);

        if (isActionAppliedForSomeIDs) {
          refreshTable();
        }

        return Promise.reject(error);
      });
  };

  return (
    <>
      <Menu>
        <Menu.Trigger className="group flex items-center gap-2 rounded border border-foundations-bold bg-foundations-default p-2">
          <span className="text-sm text-foundations-bolder">Actions</span>

          <ChevronDownIcon
            className="ml-auto h-4 w-4 shrink-0 text-foundations-subtler group-data-[popup-open]:text-info"
            aria-hidden="true"
          />
        </Menu.Trigger>

        <Menu.Popup>
          {bulkActionsPermissions?.["pause_selected"] && (
            <Menu.Item onClick={() => setOpenModal("pause")}>
              <Menu.Icon Icon={PauseIcon} />
              Pause
            </Menu.Item>
          )}
          {bulkActionsPermissions?.["resume_selected"] && (
            <Menu.Item onClick={() => setOpenModal("resume")}>
              <Menu.Icon Icon={PlayIcon} />
              Resume
            </Menu.Item>
          )}
          {bulkActionsPermissions?.["cancel_selected"] && (
            <>
              <Menu.Separator />
              <Menu.Item onClick={() => setOpenModal("cancel")}>
                <Menu.Icon Icon={XIcon} />
                Cancel
              </Menu.Item>
            </>
          )}
          {bulkActionsPermissions?.["delete_selected"] && (
            <>
              <Menu.Separator />
              <Menu.Item
                variant="destructive"
                onClick={() => setOpenModal("delete")}
              >
                <Menu.Icon Icon={TrashIcon} />
                Delete
              </Menu.Item>
            </>
          )}
        </Menu.Popup>
      </Menu>

      <AlertModal
        isOpen={openModal === "pause"}
        onClose={() => setOpenModal(null)}
        variant="warning"
        Icon={PauseIcon}
        title="Pause subscription"
        description={<>Are you sure you want to pause this subscription?</>}
        confirmLabel="Pause"
        onConfirm={() =>
          doBulkAction({ type: "pause_selected", selected: selectedIds }).then(
            () => {
              showSuccessMessage({
                message: `${selectedIds.length} Subscriptions paused successfully!`,
              });
              refreshTable();
            }
          )
        }
      />

      <AlertModal
        isOpen={openModal === "resume"}
        onClose={() => setOpenModal(null)}
        variant="success"
        Icon={PlayIcon}
        title="Resume subscription"
        description={<>Are you sure you want to resume this subscription?</>}
        confirmLabel="Resume"
        onConfirm={() =>
          doBulkAction({ type: "resume_selected", selected: selectedIds }).then(
            () => {
              showSuccessMessage({
                message: `${selectedIds.length} Subscriptions resumed successfully!`,
              });
              refreshTable();
            }
          )
        }
      />

      <CancelBulkAction
        isOpen={openModal === "cancel"}
        onClose={() => setOpenModal(null)}
        doAction={(type) =>
          doBulkAction({ type, selected: selectedIds }).then(() => {
            showSuccessMessage({
              message: `${selectedIds.length} Subscriptions ${
                type === "terminate_selected" ? "terminated" : "cancelled"
              } successfully!`,
            });
            refreshTable();
          })
        }
      />

      <AlertModal
        isOpen={openModal === "delete"}
        onClose={() => setOpenModal(null)}
        variant="danger"
        Icon={TrashIcon}
        title="Delete subscription"
        description={
          <>
            Are you sure you want to delete this subscription ? <br />
            All of this data will be removed from the dashboard.
          </>
        }
        confirmLabel="Delete"
        onConfirm={() =>
          doBulkAction({ type: "delete_selected", selected: selectedIds }).then(
            () => {
              showSuccessMessage({
                message: `${selectedIds.length} Subscriptions deleted successfully!`,
              });
              refreshTable();
            }
          )
        }
      />
    </>
  );
}

const CancelBulkAction = ({
  doAction,
  isOpen,
  onClose,
}: {
  doAction(type: string): void;
  isOpen: boolean;
  onClose(): void;
}) => {
  const [loadingAction, setLoadingAction] = useState<
    "now" | "nextPayment" | null
  >(null);

  return (
    <Dialog
      open={isOpen}
      onOpenChange={() => {
        if (loadingAction) return;
        onClose();
      }}
    >
      <Dialog.Popup className="px-4 pb-4 pt-5 sm:p-6">
        <div>
          <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-success-subtler">
            <CheckIcon className="h-6 w-6 text-success" aria-hidden="true" />
          </div>
          <div className="mt-3 text-center sm:mt-5">
            <Dialog.Title>Cancel subscription</Dialog.Title>
            <div className="mt-2">
              <Dialog.Description>
                Choose when the cancellation would be applied
              </Dialog.Description>
            </div>
          </div>
        </div>

        <Dialog.Footer variant="bare" className="mt-5 sm:mt-6">
          <Button
            colorScheme="danger"
            className="w-full justify-center text-base sm:col-start-2  sm:text-sm"
            onClick={async () => {
              setLoadingAction("now");
              try {
                await doAction("terminate_selected");
                onClose();
              } catch (error) {}
              setLoadingAction(null);
            }}
            isLoading={loadingAction === "now"}
            disabled={loadingAction === "nextPayment"}
          >
            Now
          </Button>

          <Button
            variant="secondary"
            className="mt-3 w-full justify-center sm:col-start-1 sm:mt-0 sm:text-sm"
            onClick={async () => {
              setLoadingAction("nextPayment");
              try {
                await doAction("cancel_selected");
                onClose();
              } catch (error) {}
              setLoadingAction(null);
            }}
            isLoading={loadingAction === "nextPayment"}
            disabled={loadingAction === "now"}
          >
            By next payment
          </Button>
        </Dialog.Footer>
      </Dialog.Popup>
    </Dialog>
  );
};
